/**
 * DifyAppService — 前端 Dify 智能体动态配置与聊天服务
 *
 * 路径中的 id 为本地 Agent 主键（非 Dify App UUID）。
 * 后端通过 Agent.apikey 调用 Dify Service API GET /parameters。
 *
 * 对应后端路由：
 *   GET  /api/dify/apps/                      — 管理端 App 列表
 *   GET  /api/dify/apps/{agentId}/config/     — 获取智能体动态配置
 *   POST /api/dify/apps/{agentId}/sync/        — 手动同步配置
 *   POST /api/dify/chat/{agentId}/             — 按 mode 智能路由聊天
 */
import axios from '../api';

const API_PREFIX = '/api/dify';

const forceParams = (forceRefresh) => (forceRefresh ? { force: 'true' } : {});

const DifyAppService = {
  /**
   * 获取 Dify App 列表（管理端）
   */
  async getAppList(forceRefresh = false) {
    try {
      const res = await axios.get(`${API_PREFIX}/apps/`, {
        params: forceParams(forceRefresh),
      });
      return res.data?.data || res.data || [];
    } catch (err) {
      console.error('[DifyAppService] 获取 App 列表失败:', err);
      throw err;
    }
  },

  /**
   * 获取本地智能体的 Dify 动态配置
   * @param {string|number} agentId - 本地 Agent 主键
   */
  async getAppConfig(agentId, forceRefresh = false) {
    if (!agentId) {
      throw new Error('agentId 不能为空');
    }
    try {
      const res = await axios.get(`${API_PREFIX}/apps/${agentId}/config/`, {
        params: forceParams(forceRefresh),
      });
      return res.data;
    } catch (err) {
      console.error(`[DifyAppService] 获取 Agent ${agentId} 配置失败:`, err);
      throw err;
    }
  },

  /**
   * 手动触发后端同步智能体配置
   */
  async syncAgent(agentId) {
    if (!agentId) {
      throw new Error('agentId 不能为空');
    }
    const res = await axios.post(`${API_PREFIX}/apps/${agentId}/sync/`);
    return res.data;
  },

  /**
   * 获取对话记录列表（后端代理，避免浏览器直连 Dify CORS）
   */
  async listConversations(agentId, { user_id, last_id = '', limit = 50 } = {}) {
    if (!agentId) {
      throw new Error('agentId 不能为空');
    }
    try {
      const res = await axios.get(`${API_PREFIX}/chat/${agentId}/conversations/`, {
        params: { user_id, last_id, limit },
      });
      return res.data;
    } catch (err) {
      console.error(`[DifyAppService] 获取对话记录失败 (agentId=${agentId}):`, err);
      throw err;
    }
  },

  /**
   * 获取会话历史消息（后端代理）
   */
  async listMessages(agentId, { conversation_id, user_id, first_id = '', limit = 50 } = {}) {
    if (!agentId || !conversation_id) {
      throw new Error('agentId 和 conversation_id 不能为空');
    }
    try {
      const res = await axios.get(`${API_PREFIX}/chat/${agentId}/messages/`, {
        params: { conversation_id, user_id, first_id, limit },
      });
      return res.data;
    } catch (err) {
      console.error(`[DifyAppService] 获取会话消息失败 (agentId=${agentId}):`, err);
      throw err;
    }
  },

  /**
   * 删除会话（后端代理）
   */
  async deleteConversation(agentId, { conversation_id, user_id } = {}) {
    if (!agentId || !conversation_id) {
      throw new Error('agentId 和 conversation_id 不能为空');
    }
    try {
      const res = await axios.delete(
        `${API_PREFIX}/chat/${agentId}/conversations/${conversation_id}/`,
        { params: { user_id } },
      );
      return res.data;
    } catch (err) {
      console.error(`[DifyAppService] 删除会话失败 (agentId=${agentId}):`, err);
      throw err;
    }
  },

  /**
   * 发送聊天消息（后端 DifyAdapter 按 mode 路由，使用 Agent.apikey）
   */
  async chat(agentId, { query, inputs = {}, files = [], conversation_id, user_id, response_mode = 'blocking' }) {
    if (!agentId) {
      throw new Error('agentId 不能为空');
    }
    try {
      const res = await axios.post(`${API_PREFIX}/chat/${agentId}/`, {
        query,
        inputs,
        files,
        conversation_id,
        user_id,
        response_mode,
      });
      return res.data;
    } catch (err) {
      console.error(`[DifyAppService] 聊天请求失败 (agentId=${agentId}):`, err);
      throw err;
    }
  },

  /**
   * 清除后端配置缓存
   */
  async clearCache(agentId = null) {
    try {
      const url = agentId
        ? `${API_PREFIX}/apps/${agentId}/clear-cache/`
        : `${API_PREFIX}/apps/clear-cache/`;
      const res = await axios.post(url);
      return res.data;
    } catch (err) {
      console.warn('[DifyAppService] 清除缓存失败:', err);
      return { success: false };
    }
  },
};

export default DifyAppService;
